import { horizontalListSortingStrategy, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import classNames from "classnames";

import { ActionIcon } from "../../action_icon";
import { Stack } from "../../containers";
import { Tile } from "../../tile";
import Toggle from "../../toggle/toggle";
import { Typography } from "../../typography";
import { SortableListProps } from "../sortable_list";
import css from "../sortable_list.module.scss";
import { ListAction, SortableListItem } from "../sortable_list.types";

type SortableItemProps = SortableListItem & {
  disabled?: boolean;
  sortDisabled?: boolean;
  onRemove: (id: string) => void;
  onToggle?: (id: string, value: boolean) => void;
  renderAction?: SortableListProps["renderAction"];
  strategy: typeof horizontalListSortingStrategy | typeof verticalListSortingStrategy;
  action?: Omit<ListAction, ListAction.Sort>;
  classes?: SortableListProps["classes"];
};

export const SortableItem = ({
  onRemove,
  onToggle,
  renderAction,
  strategy,
  action,
  disabled,
  classes,
  ...item
}: SortableItemProps) => {
  const { label, subtitle, id, toggled, actionDisabled, sortDisabled, qaId } = item;
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id,
    disabled: sortDisabled
  });

  const style = {
    transform: transform
      ? `translate3d(${strategy === horizontalListSortingStrategy ? transform.x : 0}px, ${
          strategy === verticalListSortingStrategy ? transform.y : 0
        }px, 0)`
      : undefined
  };

  return (
    <Tile
      ref={setNodeRef}
      className={classNames(css.cell, classes?.item)}
      style={style}
      paddingVariant="md"
      border
    >
      {!disabled && (
        <ActionIcon
          name={
            strategy === horizontalListSortingStrategy ? "arrow-2-directions" : "arrow-2-directions-vertical"
          }
          {...listeners}
          {...attributes}
          disabled={sortDisabled}
          size="xxs"
          cursor="pointer"
          classes={{ root: classes?.dragIcon, icon: css.dragHandle }}
        />
      )}
      <Stack className={classNames(css.label, { [css.offsetLeft]: disabled })} gap={null}>
        {typeof label === "string" && (
          <Typography variant="bodyBold" textOverflow="clip" title={label}>
            {label}
          </Typography>
        )}
        {typeof label !== "string" && label}
        {subtitle && (
          <Typography variant="bodyRegular" textOverflow="clip" title={subtitle}>
            {subtitle}
          </Typography>
        )}
      </Stack>
      {action === ListAction.Removal && (
        <ActionIcon
          name="cross"
          size="xxs"
          onClick={() => onRemove(id)}
          disabled={actionDisabled}
          qaId={qaId}
        />
      )}
      {action === ListAction.Toggle && (
        <Toggle
          onChange={(value) => onToggle?.(id, value)}
          checked={toggled}
          disabled={actionDisabled}
          qaId={qaId}
          compact
        />
      )}
      {action === ListAction.Custom && renderAction?.(item)}
    </Tile>
  );
};
